/** Shared primitives for case-insensitive substring search ranking and highlighting. */

/** How well a value matches a search term. Lower is better. */
export const MatchTier = {
  Exact: 0,
  StartsWith: 1,
  EndsWith: 2,
  Contains: 3,
} as const;
export type MatchTier = (typeof MatchTier)[keyof typeof MatchTier];

/**
 * Classifies a value that is already known to contain `search` at `matchIndex`.
 * `value` and `search` must already be normalized to the same case.
 */
export function getMatchTier(value: string, search: string, matchIndex: number): MatchTier {
  if (value === search) {
    return MatchTier.Exact;
  }
  if (matchIndex === 0) {
    return MatchTier.StartsWith;
  }
  if (value.endsWith(search)) {
    return MatchTier.EndsWith;
  }
  return MatchTier.Contains;
}

/**
 * Inclusive `[start, end]` ranges for every non-overlapping, case-insensitive occurrence of
 * `search` in `value`, so highlighting covers all matched ranges rather than only the first.
 * Returns `[]` when either side is missing or empty, or when there is no match, so callers can
 * pass optional model fields straight through at render time.
 */
export function getSubstringMatchIndices(
  value: string | null | undefined,
  search: string | null | undefined
): [number, number][] {
  if (!value || !search) {
    return [];
  }

  const normalizedValue = value.toLowerCase();
  const normalizedSearch = search.toLowerCase();
  const indices: [number, number][] = [];

  for (
    let matchIndex = normalizedValue.indexOf(normalizedSearch);
    matchIndex >= 0;
    matchIndex = normalizedValue.indexOf(normalizedSearch, matchIndex + normalizedSearch.length)
  ) {
    indices.push([matchIndex, matchIndex + normalizedSearch.length - 1]);
  }

  return indices;
}
